import React, { useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import {
  IconAdd,
  IconBarcode,
  IconGrid,
  IconHelp,
  IconSearch,
  IconSettings,
  IconTest,
  IconUser,
  IconWorkOrder,
  IconWrench,
} from '../../../../Lib/Icons';
import { SimpleSearch } from '../../../Search';
import Barcode from '../Barcode';
import { BulkEntryNav } from '../BulkEntryNav';
import SlideOutMenu from '../SlideOutMenu';
import './AppNav.scss';

const isDevMode = process.env.REACT_APP_ENABLE_DEV_MODE === 'true';

type CloseHandle = {
  close: () => void;
};

enum NavSlideOut {
  None,
  Search,
  BulkEntry,
  Barcode,
}

const AppNav = () => {
  const adminSettingsClaim = useSelector(
    (state) => state.user.data?.Claims && state.user.data?.Claims['sq.administrator.settings']
  );
  const agsPermission = useSelector((state) => state.user.data?.Claims && state.user.data?.Claims['sq.ags.export']);
  const platformVersion: string[] = useSelector((state) => state.authentication.platformVersion)?.split('.');
  const isAnyDialogActive = useSelector((state) => state.app.activeDialogs.length > 0);
  const [slideOutMenu, setSlideOutMenu] = useState(NavSlideOut.None);
  const ref = useRef<CloseHandle>();
  const location = useLocation();
  useEffect(() => {
    ref.current?.close();
    setSlideOutMenu(NavSlideOut.None);
  }, [location]);

  useHotkeys('escape', () => setSlideOutMenu(NavSlideOut.None), {
    enableOnFormTags: ['INPUT', 'TEXTAREA', 'SELECT'],
  });

  useHotkeys(
    '/',
    (event) => {
      if (slideOutMenu !== NavSlideOut.Search) {
        setSlideOutMenu(NavSlideOut.Search);
        event.preventDefault();
      }
    },
    { enabled: !isAnyDialogActive }
  );

  const showSlideOutMenuOnClick = (slideOut: NavSlideOut) => (event: React.MouseEvent<HTMLDivElement>) => {
    if (!event.defaultPrevented) {
      setSlideOutMenu(slideOut);
      event.stopPropagation();
    }
  };

  const showSlideOutMenuOnEnterKey = (slideOut: NavSlideOut) => (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.defaultPrevented) {
      setSlideOutMenu(slideOut);
      event.stopPropagation();
    }
  };

  return (
    <nav className="AppNav">
      <SlideOutMenu
        closeMenu={() => {
          ref.current?.close();
          setSlideOutMenu(NavSlideOut.None);
        }}
        visible={slideOutMenu !== NavSlideOut.None}
      >
        {slideOutMenu === NavSlideOut.Search && <SimpleSearch />}
        {slideOutMenu === NavSlideOut.BulkEntry && (
          <div style={{ paddingTop: '20px' }}>
            <BulkEntryNav />
          </div>
        )}
        {slideOutMenu === NavSlideOut.Barcode && (
          <div style={{ paddingTop: '20px' }}>
            <Barcode
              parentRef={ref}
              closeMenu={() => {
                ref.current?.close();
                setSlideOutMenu(NavSlideOut.None);
              }}
            />
          </div>
        )}
      </SlideOutMenu>

      <div className="AppNav-Start">
        <NavLink to="/" className="AppNav-Item AppNav-Item-home">
          Home
        </NavLink>
        <div
          className="AppNav-Item"
          tabIndex={0}
          onClick={showSlideOutMenuOnClick(NavSlideOut.Barcode)}
          onKeyDown={showSlideOutMenuOnEnterKey(NavSlideOut.Barcode)}
        >
          <IconBarcode title="Barcode" size="32px" />
        </div>
        <div
          className="AppNav-Item"
          tabIndex={0}
          onClick={showSlideOutMenuOnClick(NavSlideOut.BulkEntry)}
          onKeyDown={showSlideOutMenuOnEnterKey(NavSlideOut.BulkEntry)}
        >
          <IconGrid title="Bulk Entry" size="26px" />
        </div>
        <div
          className="AppNav-Item"
          tabIndex={0}
          onClick={showSlideOutMenuOnClick(NavSlideOut.Search)}
          onKeyDown={showSlideOutMenuOnEnterKey(NavSlideOut.Search)}
        >
          <IconSearch title="Search" size="34px" />
        </div>
        {isDevMode && (
          <NavLink to="/addWorksheet" className="AppNav-Item">
            <IconAdd title="Add Worksheet" size="28px" />
          </NavLink>
        )}
        {isDevMode && agsPermission && (
          <NavLink to="/ags/workorders" className="AppNav-Item">
            <IconWorkOrder title="WorkOrders" size="28px" />
          </NavLink>
        )}
      </div>

      <div className="AppNav-End">
        {isDevMode && (
          <NavLink to="/toolkit" className="AppNav-Item">
            <IconWrench title="Toolkit" size="26px" />
          </NavLink>
        )}
        {isDevMode && (
          <NavLink to="/test" className="AppNav-Item">
            <IconTest title="Test" size="26px" />
          </NavLink>
        )}
        {platformVersion && (
          <a
            className="AppNav-Item"
            href={`https://spectraqest.atlassian.net/wiki/spaces/QPD${platformVersion[0]}${platformVersion[1]}/overview`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <IconHelp title="Help" size="26px" />
          </a>
        )}
        {adminSettingsClaim && (
          <NavLink to="/admin" className="AppNav-Item">
            <IconSettings title="Settings" size="28px" />
          </NavLink>
        )}
        <NavLink to="/user" className="AppNav-Item">
          <IconUser title="User" size="28px" />
        </NavLink>
      </div>
    </nav>
  );
};

export default AppNav;
